import React from 'react';
import { Check, Heart, Sparkle, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@components/ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@hooks/use-mobile';

interface PricingCardProps {
  productName: string;
  priceId: string;
  price: number;
  features: string[];
  isSelected?: boolean;
  onSelect?: (priceId: string) => void;
  originalPrice?: number;
  pricePerPhoto?: string;
  pricePerPhotoAmount?: string;
  isDiscounted?: boolean;
  discountPercentage?: number;
  showCents?: boolean;
  variant?: 'select' | 'direct';
  onBuy?: (priceId: string) => void;
  isLoading?: boolean;
  buttonText?: string;
}

const formatPrice = (price: number, showCents: boolean = true) => {
  return showCents ? price.toFixed(2) : Math.floor(price);
};

export function PricingCard({
  productName,
  priceId,
  price,
  features,
  isSelected,
  onSelect,
  originalPrice,
  pricePerPhoto,
  pricePerPhotoAmount,
  isDiscounted = false,
  discountPercentage = 0,
  variant = 'select',
  onBuy,
  isLoading = false,
  buttonText
}: PricingCardProps) {
  const isMobile = useIsMobile();

  const handleCardClick = () => {
    if (variant === 'select' && onSelect) {
      onSelect(priceId);
    }
  };

  const handleBuyClick = (e: React.MouseEvent) => {
    if (onBuy) {
      e.stopPropagation();
      onBuy(priceId);
    }
  };

  const renderBadge = () => {
    if (productName === 'Pro Bundle') {
      return (
        <Badge
          variant="success"
          className={cn(
            'w-fit flex items-center gap-1 text-[10px]',
            variant === 'direct' && isMobile ? 'mt-3' : 'lg:absolute top-2 right-2'
          )}
        >
          <Heart size={8} className="fill-current" /> 84% pick this
        </Badge>
      );
    }

    if (productName === 'Pro+ Bundle') {
      return (
        <Badge
          variant="accent"
          className={cn(
            'w-fit flex items-center gap-1 text-[10px]',
            variant === 'direct' && isMobile ? 'mt-3' : 'lg:absolute top-2 right-2'
          )}
        >
          <Sparkle size={8} className="fill-current" /> Best Value
        </Badge>
      );
    }

    return null;
  };

  return (
    <Card
      className={cn(
        'relative transition-colors',
        variant === 'select' && 'cursor-pointer focus:outline-none',
        variant === 'select' && isSelected && 'ring-2 ring-accent ring-offset-2 dark:ring-offset-background'
      )}
      onClick={handleCardClick}
      tabIndex={variant === 'select' ? 0 : undefined}
      role={variant === 'select' ? 'radio' : undefined}
      aria-checked={variant === 'select' ? isSelected : undefined}
      aria-label={variant === 'select' ? `Select ${productName}` : undefined}
      onKeyDown={
        variant === 'select'
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                onSelect?.(priceId);
              }
            }
          : undefined
      }
    >
      <div className="p-4 lg:p-6">
        <div className="flex flex-row lg:flex-col gap-4">
          <div className="w-[40%] lg:w-full">
            <h3 className="text-base lg:text-lg font-semibold">{productName}</h3>

            <div className="flex flex-col">
              {/* Discount percentage and main price */}
              <div className="flex items-baseline gap-2">
                {isDiscounted && discountPercentage > 0 && (
                  <span className="text-red-500 text-xl lg:text-2xl">-{discountPercentage}%</span>
                )}
                <span className="text-2xl lg:text-2xl font-semibold">${formatPrice(price)}</span>
              </div>

              {/* Original price with "Typical:" label */}
              {isDiscounted && originalPrice && (
                <div className="text-xs lg:text-base text-muted-foreground">Typical: ${formatPrice(originalPrice)}</div>
              )}
            </div>
          </div>

          <div className="w-[60%] lg:w-full lg:mt-0">
            <ul
              role="list"
              className="text-xs lg:text-base leading-5 lg:leading-6 text-foreground space-y-1 lg:space-y-2 mb-2 lg:mb-4"
            >
              {features.slice(0, isMobile ? -2 : undefined).map((feature, featureIndex) => (
                <li key={feature} className="flex items-center gap-1 text-muted-foreground">
                  <Check
                    strokeWidth={2}
                    className={cn(
                      'w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2 flex-shrink-0',
                      productName === 'Pro Bundle' ? 'text-green-600 dark:text-green-300' : 'text-muted-foreground'
                    )}
                  />
                  <span className="text-xs lg:text-base whitespace-nowrap">
                    {feature}
                    {featureIndex === 1 && pricePerPhoto && (
                      <span className="text-green-600 dark:text-green-300">
                        {isDiscounted ? ` $${pricePerPhotoAmount} / photo` : ` ${pricePerPhoto}`}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            {/* Show badge before button on mobile direct variant */}
            {variant === 'direct' && isMobile && renderBadge()}

            {variant === 'direct' && (
              <div className={cn('mt-4', isMobile && 'mb-2')}>
                <Button
                  className="w-full"
                  variant={productName === 'Pro Bundle' ? 'accent' : 'outline'}
                  size={isMobile ? 'default' : 'sm'}
                  onClick={handleBuyClick}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    buttonText || `Buy ${productName}`
                  )}
                </Button>
              </div>
            )}

            {/* Only show badge here on desktop or select variant */}
            {(!isMobile || variant === 'select') && renderBadge()}
          </div>
        </div>
      </div>
    </Card>
  );
}
